/* eslint-disable react/jsx-pascal-case */
import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import "../App.css";
import { createTheme, MuiThemeProvider } from "@material-ui/core/styles";
import { MuiTheme } from "../AppConfig";
import DateFnsUtils from "@date-io/date-fns";
import { MuiPickersUtilsProvider } from "@material-ui/pickers";
import { Backdrop, Button, CircularProgress, Dialog, Typography } from "@material-ui/core";
import TopBar from "../components_main/TopBar";
import Main from "../components_main/Main";
import Designer from "../components_designer/Designer";
import { ClosePaper } from "../components_designer/layout/PaperFunctions";
import LogInDialog from "../components_main/LogInDialog";
import DragDrop from "../components_main/DragDrop";
import Announcements from "../components_main/Announcements";
import ErrorBoundary from "../ErrorBoundary";
import BSL_Dialog from "../components/BSL_Dialog";
import BSL_LoadingButton from "../components/BSL_LoadingButton";
import { LogOut } from "../components_main/ManageUser";
import { BabylonSceneProvider } from "../components_designer/layout/design_cloud_3d_model/BabylonSceneProvider";
import { MAIN_PAGE, DESIGNER_PAGE, changePage, SetAppDialogMessage, saveUserToWeb } from "../redux_store/appState";
import TermsConditions, { TermsModalContent, updateRenewDate } from "../hooks/app/useTermsConditions";
import usePriceFile from "../hooks/app/usePriceFile";
import usePriceRise from "../hooks/app/usePriceRise";
import useBomTest from "../hooks/app/useBomTest";
import useAutoReload from "../hooks/app/useAutoReload";
import usePageLayout from "../hooks/app/usePageLayout";
import usePageNavigation from "../hooks/app/usePageNavigation";
import { AppRedirects, CheckCookies, HandlePageChange, loadDefaultJob } from "../services/appServices";
import { deckEngineering_test } from "../components_designer/specification_drawer/decks/DeckEngineering";

// Material UI Theme
const theme = createTheme(MuiTheme({ FIELDERS: window.F }));
window.__MUI_USE_NEXT_TYPOGRAPHY_VARIANTS__ = true;

export default function DesignCloud() {
    const dispatch = useDispatch();

    // Redux State
    const app = useSelector((state) => state.app);
    const job = useSelector((state) => state.job);
    const config = useSelector((state) => state.config);
    const offerings = useSelector((state) => state.app.offerings);
    const { page, user, logged_user_code, online } = app;

    // User Details
    const user_status = user?.Status;
    const business_status = user?.BusinessDetails?.Status;
    const business_name = user?.BusinessDetails?.Name;
    const agreement = user?.license_agreement;

    // Local State
    const [jobToLoad, setJobToLoad] = useState("");
    const [loadingDefaultJob, setLoadingDefaultJob] = useState(false);
    const [licenseOpen, setLicenseOpen] = useState(false);
    const [loadingUser, setLoadingUser] = useState(false);
    const [allowAccept, setAllowAccept] = useState(false);
    const [loadedDefaultState, setLoadedDefaultState] = useState(false);
    const [bomTestResult, setBomTestResult] = useState("STARTING UP");
    const [warningMessages, setWarningMessages] = React.useState([]);

    // Check Cookies
    CheckCookies({ online, config, dispatch });

    // Hooks
    // usePriceFile({ dispatch });
    usePriceRise({ dispatch });
    useBomTest({ user, app, bomTestResult, setBomTestResult });
    TermsConditions({ logged_user_code, user, agreement, config, dispatch, saveUserToWeb, setLicenseOpen });
    useAutoReload();
    usePageLayout({ page, dispatch, ClosePaper });
    usePageNavigation({ dispatch });

    // Load Default Job
    useEffect(() => {
        if (jobToLoad && !loadingDefaultJob && logged_user_code && user && !job && offerings) {
            setLoadingDefaultJob(true);
            loadDefaultJob({ jobid: jobToLoad, dispatch, user });
            setJobToLoad(null);
        }
    }, [user, offerings, logged_user_code]);

    useEffect(() => {
        HandlePageChange({
            dispatch,
            setJobToLoad,
            setLoadingDefaultJob,
            loadingDefaultJob,
            logged_user_code,
            loadedDefaultState,
            setLoadedDefaultState,
            user,
            page,
        });
    }, []);

    const handleScroll = (e) => {
        const bottom = e.target.scrollHeight - e.target.scrollTop - 20 <= e.target.clientHeight;
        if (bottom) setAllowAccept(true);
    };

    // Debugging for Specific Users
    if (
        (user?.id.includes("kevinchan") || user?.id.includes("michael.abdo")) &&
        window.debug &&
        window.location.href.includes("?dev")
    ) {
        deckEngineering_test();
    }

    // Prevent License Bypass
    if (licenseOpen && !((page === null || page === MAIN_PAGE) && user)) {
        dispatch(changePage(MAIN_PAGE));
    }

    return (
        AppRedirects({ user, theme, bomTestResult }) ?? (
            <ErrorBoundary>
                <MuiThemeProvider theme={theme}>
                    <MuiPickersUtilsProvider utils={DateFnsUtils}>
                        <div className={"App"} style={{ background: window.LYSAGHT ? "#F2F2F0" : "auto" }}>
                            <TopBar page={page} />
                            <Announcements warningMessages={warningMessages} />
                            {!online && (
                                <Backdrop
                                    style={{
                                        zIndex: 10100,
                                        color: "#fff",
                                    }}
                                    open={!online}
                                >
                                    <CircularProgress color="inherit" style={{ position: "absolute" }} />
                                    <br />
                                    <Typography variant="overline" gutterBottom style={{ marginTop: "100px" }}>
                                        {window.tokenexpired > 0
                                            ? "Cannot contact server... login token may be expired..."
                                            : "Cannot contact server... waiting for connection..."}
                                    </Typography>
                                </Backdrop>
                            )}
                            {offerings &&
                                business_status &&
                                business_status !== "ACTIVE" &&
                                user_status &&
                                user_status === "ACTIVE" && (
                                    <Backdrop
                                        style={{
                                            zIndex: 10100,
                                            color: "#fff",
                                        }}
                                        open={true}
                                    >
                                        <Typography variant="overline" gutterBottom>
                                            Business Details for
                                            <br />
                                            <span style={{ fontSize: "24px" }}>{business_name}</span>
                                            <br />
                                            need to be set up.
                                            <br />
                                            Please contact your Sales Rep to Complete Setup.
                                        </Typography>
                                    </Backdrop>
                                )}
                            {offerings &&
                                (user_status !== "ACTIVE" || !user_status) &&
                                !window.location.href.includes("static") && (
                                    <Backdrop
                                        style={{
                                            zIndex: 9999,
                                            color: "#fff",
                                        }}
                                        open={true}
                                    >
                                        <Typography variant="overline" gutterBottom>
                                            Your User Profile
                                            <br />
                                            <span style={{ fontSize: "24px" }}>{user?.DisplayName}</span>
                                            <br />
                                            is not active.
                                            <br />
                                            Please contact your Sales Rep to Setup.
                                        </Typography>
                                    </Backdrop>
                                )}

                            {!logged_user_code && online && !window.location.href.includes("static") && (
                                <LogInDialog config={config} />
                            )}

                            {!window.location.href.includes("static") && !jobToLoad && (
                                <Main visible={(page === null || page === MAIN_PAGE) && user} />
                            )}
                            {page === DESIGNER_PAGE && logged_user_code && (
                                <BabylonSceneProvider>
                                    <Designer
                                        warningMessages={warningMessages}
                                        setWarningMessages={setWarningMessages}
                                    />
                                </BabylonSceneProvider>
                            )}
                            {page !== DESIGNER_PAGE && logged_user_code && window.FIELDERS && <DragDrop />}

                            <BSL_Dialog
                                open={app?.MAIN_PAGE?.Message}
                                onClose={() => {
                                    dispatch(SetAppDialogMessage(""));
                                }}
                                button1Text="Close"
                                button1Click={() => {
                                    dispatch(SetAppDialogMessage(""));
                                }}
                            >
                                {app?.MAIN_PAGE?.Message}
                            </BSL_Dialog>
                        </div>
                    </MuiPickersUtilsProvider>
                </MuiThemeProvider>
                <Dialog
                    open={licenseOpen}
                    aria-labelledby="modal-modal-title"
                    aria-describedby="modal-modal-description"
                >
                    <div style={{ padding: "20px" }}>
                        <h2>License Agreement</h2>
                        <p>In order to continue please accept the License Agreement Below.</p>
                        <div onScroll={handleScroll} style={{ height: "450px", overflowY: "scroll" }}>
                            <TermsModalContent />
                        </div>
                    </div>
                    <div style={{ margin: "0px 20px" }}>
                        <p style={{ margin: "0px" }}>
                            User may be required to accept the terms and any updated terms on annual basis
                        </p>
                    </div>
                    <div style={{ justifyContent: "space-between", display: "flex", margin: "10px" }}>
                        <Button
                            style={{ color: "#8d8d8d", font: "400 14px 'canada-type-gibson', sans-serif" }}
                            onClick={(e) => {
                                LogOut({ dispatch });
                            }}
                        >
                            Reject and Logout
                        </Button>
                        <BSL_LoadingButton
                            style={{ width: "80px" }}
                            disabled={!allowAccept}
                            loading={loadingUser}
                            onClick={(e) => {
                                setLoadingUser("true");
                                updateRenewDate({
                                    agreement,
                                    config,
                                    user,
                                    dispatch,
                                    saveUserToWeb,
                                    addHistory: true,
                                }).then((e) => {
                                    setLicenseOpen(false);
                                    setLoadingUser(false);
                                    HandlePageChange({
                                        force: true,
                                        dispatch,
                                        setJobToLoad,
                                        setLoadingDefaultJob,
                                        loadingDefaultJob,
                                        logged_user_code,
                                        loadedDefaultState,
                                        setLoadedDefaultState,
                                        user,
                                        page,
                                    });
                                    window.LYSAGHT && window.Intercom("trackEvent", "License Agreement Accepted");
                                });
                            }}
                        >
                            Accept
                        </BSL_LoadingButton>
                    </div>
                </Dialog>

                {window.LYSAGHT &&
                    window.Intercom("update", {
                        app_id: "nxhqnktw",
                        email: app?.user?.Email,
                        name: app?.user?.DisplayName,
                        user_id: app?.user?.id,
                        phone: app?.user?.BusinessDetails?.Phone,
                        userApplication: "LLD",
                        company: {
                            company_id: app?.user?.BusinessCode,
                        },
                    })}
            </ErrorBoundary>
        )
    );
}
